import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { prefixUrl } from '../../utils/url.util';

export interface EventqSplitMenuButtonOption {
  readonly id: string;
  readonly label: string;
  readonly iconSrc?: string;
  /** When true, option is visible but not selectable. */
  readonly disabled?: boolean;
}

@Component({
  selector: 'eventq-split-menu-button',
  templateUrl: './split-menu-button.html',
  styleUrl: './split-menu-button.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventqSplitMenuButton {
  private readonly host = inject(ElementRef<HTMLElement>);

  readonly label = input.required<string>();
  readonly disabled = input(false);
  readonly iconSrc = input<string | undefined>(undefined);
  /** Trailing chevron image; rotates when the menu opens. */
  readonly chevronSrc = input<string | undefined>(undefined);
  readonly triggerTestId = input('create-incident-report');
  readonly options = input<readonly EventqSplitMenuButtonOption[]>([]);
  readonly menuOpen = signal(false);
  readonly optionSelect = output<string>();

  /** Glyphs are masked, so the icon paints in the label color rather than its own fill. */
  iconStyle(iconSrc: string): string {
    return `url(${prefixUrl(iconSrc)})`;
  }

  toggleMenu(): void {
    if (this.disabled()) {
      return;
    }
    this.menuOpen.update((open) => !open);
  }

  selectOption(id: string): void {
    const option = this.options().find((item) => item.id === id);
    if (!option || option.disabled) {
      return;
    }
    this.optionSelect.emit(id);
    this.menuOpen.set(false);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.menuOpen()) {
      return;
    }
    const target = event.target as Node | null;
    if (target && !this.host.nativeElement.contains(target)) {
      this.menuOpen.set(false);
    }
  }
}
